import React, { lazy } from 'react';
import Snackbar from '@tencent/dui/esm/components/Snackbar';
import {
    IConvertOptions,
    IShowThemeModalProps,
    IThemeColorManager,
} from '@src/slide/application/service/theme-color-manager/index.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import _set from 'lodash/set';
import {
    ICTTableCell,
    ICTTextBody,
    IPresentationDataModel,
    TableDataModel,
} from '@tencent/slide-sdk-model';
import PresentationModel from '@src/slide/domain/ui-model/element-modeles/presentation-model';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { UiDataService } from '@src/slide/domain/ui-model/service/ui-data-service';
import {
    ISlideRenderStateManager,
} from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';
import {
    computed,
    observable,
    reaction,
} from 'mobx';
import {
    IThemeModel,
    IThemeSelectItem,
} from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';
import { IReactionDisposer } from 'mobx/lib/internal';
import {
    CLASSIC_THEME,
    COLOR_MAP,
    IColorMapItem,
} from '@src/slide/application/service/theme-color-manager/theme-config';
import {
    IParagraph,
    ITextRun,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import {
    ISlideContentEnumeratorService,
} from '@src/slide/application/service/slide-content-enumerator/slide-content-enumerator-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { transaction } from '@src/slide/domain/slide-app';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { SolidFill } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';
import { STColorSchemeIndex, STSchemeColorVal } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import { IHSL, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IGradFill, ISolidFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import cloneDeep from 'lodash/cloneDeep';
import {
    TableCellPropertyConverter,
} from '@src/slide/domain/ui-model/converter/node-property/element/table-cell-property-converter';
import {
    changeArrAppendToSetOrModify,
} from '@src/slide/infrastructure/module/independent/object/arr-append-to-set-or-modify';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { TextConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-converter';
import TableModel from '@src/slide/domain/ui-model/element-modeles/table-model';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import LazyBoundary from '@com/LazyBoundary';
import {
    scheduleAfterFirstSlideDisplay,
    SlideDataServices,
} from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IThemeColorDialogProps } from '@src/slide/application/service/theme-color-manager/theme-color-dialog';
import { CommandSource } from '@tencent/slide-sdk-model/src/manager/command-manager';
import i18n from '@i18next/i18n';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';

export interface IThemeColorManagerProps {
    uiDataService: IUiDataService;
    selectionService: ISelectionService;
    slideRenderStateManager: ISlideRenderStateManager
}

const LOCAL_SLIDE_CUSTOM_THEME_ID = 'local_slide_custom_theme_id';
const LOW_SATURATION_NUMBER = 50;

/**
 * 主题色管理
 */
export class ThemeColorManager implements IThemeColorManager {
    // 主题列表弹窗
    @observable public themeColorDialogVisible = false;
    // 自定义主题弹窗
    @observable public themeColorCustomDialogVisible = false;
    // 用户自定义主题，跟客户端localstorage走，不跟用户账户走
    @observable public originCustomColorList: Array<IThemeSelectItem> = [];
    // 当前页主题
    @observable public currentThemeModel: IThemeModel;
    // 是否是暗色主题，无设置入口，一般是office导入的
    @observable public isDark = false;
    // 自定义主题面板tab的状态，未来会对应多tab
    public isOpenCustomPanel = false;
    // 当前编辑的主题
    public currentEditThemeKey: string;
    // 主题选择回调
    public onSelect?;
    // 主题取消回调
    public onCancel?;
    // sdk服务
    public newPresentationStoreService: any;
    // 克隆的ui，用于预览等临时展示
    public cloneUiDataService: IUiDataService | null;
    // 文档实际的ui
    private uiDataService: IUiDataService;
    // 主题化标记，在做主题切换时，主题化在第一次切换时做就行了
    private isConvert: boolean;
    // todo：依赖的服务后续放到一个service对象下吧，好管理interface
    private selectionService: ISelectionService;
    private presentation: IPresentationDataModel | null;
    private slideRenderStateManager: ISlideRenderStateManager;
    private slideContentEnumeratorService: ISlideContentEnumeratorService;
    private reaction: IReactionDisposer;
    private LazyImportDialogComponent = lazy(() => scheduleAfterFirstSlideDisplay(() => import(/* webpackChunkName: "lazyComponents" */ './theme-color-dialog')));

    constructor(props: IThemeColorManagerProps) {
        const { uiDataService, selectionService, slideRenderStateManager } = props;
        this.uiDataService = uiDataService;
        this.selectionService = selectionService;
        this.slideRenderStateManager = slideRenderStateManager;
        this.customColorList = this.getAllCustomTheme();
        this.initReaction();
    }

    /**
     * 主题色切换弹窗
     * @param props
     */
    public getLazyComponent = (props: Omit<IThemeColorDialogProps, 'themeColorManager'>) => {
        const { LazyImportDialogComponent } = this;
        return <LazyBoundary>
            <LazyImportDialogComponent
                {...props}
                themeColorManager={this}
            />
        </LazyBoundary>;
    };

    /**
     * todo：依赖的服务后续放到一个service对象下吧，好管理interface
     * @param slideContentEnumeratorService
     */
    public setSlideContentEnumeratorService = (slideContentEnumeratorService: ISlideContentEnumeratorService) => {
        this.slideContentEnumeratorService = slideContentEnumeratorService;
    };

    /**
     * clone一个uiData，用于切换主题时的预览
     */
    public copyPresentation = () => {
        this.presentation = this.uiDataService.presentation.sdkData as IPresentationDataModel;
        this.newPresentationStoreService = SlideDataServices.SDKDataServices.PresentationStoreService()
            ?.createFromData(this.presentation.getCloneData());
        const newSdkPresentation = this.newPresentationStoreService?.presentation;

        const newPresentation = new PresentationModel({
            selectionService: this.selectionService,
        });
        newPresentation.bindSDK(newSdkPresentation);
        this.cloneUiDataService = new UiDataService();
        this.cloneUiDataService.presentation = newPresentation;
        requestAnimationFrame(() => {
            this.updateThemeColorDialogVisible(true);
        });
    };

    /**
     * 根据id应用主题切换
     * @param id
     */
    public applyThemeColor = (id: string) => {
        let updateThemeProps: IThemeSelectItem | undefined;
        const themeList = [...CLASSIC_THEME, ...this.customColorList];

        themeList.forEach((theme) => {
            if (theme.id === id) {
                updateThemeProps = theme;
            }
        });

        if (updateThemeProps) {
            transaction(() => {
                this.uiDataService.presentation.themeList?.forEach((themeModel) => {
                    updateThemeProps && themeModel.updateTheme(updateThemeProps);
                });
            });
        }
    };

    /**
     * 关闭对话框时要删除clone的uiData
     */
    public destroyCopySlideApp = () => {
        this.presentation = null;
        this.newPresentationStoreService = null;
        this.cloneUiDataService = null;
    };

    /**
     * 打开图表主题切换引导
     * 一个客户端只来一次
     */
    public showThemeTips() {
        Snackbar.show({
            message: i18n.t('图表配色可基于主题色更新'),
            type: 'info',
            duration: 6000,
            action: i18n.t('去切换'),
            onClickAction: () => this.showThemeModal(),
        });
    }

    /**
     * 打开主题切换弹窗
     */
    public showThemeModal(props?: IShowThemeModalProps) {
        if (props?.onSelect) {
            this.onSelect = props.onSelect;
        }
        if (props?.onCancel) {
            this.onCancel = props.onCancel;
        }
        if (props?.source) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.COLOR_SWITCH,
                action: TDWActionType.THEME_VIEW,
                ver5: props.source,
            });
        }
        this.isOpenCustomPanel = !!props?.showCustomTab;
        this.copyPresentation();
    }

    /**
     * 更新弹窗状态
     * 清空主题转换标记
     * @param visible
     */
    public updateThemeColorDialogVisible(visible: boolean) {
        this.themeColorDialogVisible = visible;
        requestAnimationFrame(() => {
            if (!visible) {
                this.destroyCopySlideApp();
                this.clearAICallback();
            }
        });
        this.isConvert = false;
    }

    /**
     * 更新关闭主题色弹窗
     * @param visible
     */
    public updateThemeColorCustomDialogVisible(visible: boolean) {
        this.themeColorCustomDialogVisible = visible;
    }

    /**
     * 后续增加主题还原用的，不过从模版切换实现看来，sdk的undo不适用预览，要考虑从销毁重渲染来做
     * @param callback
     */
    public addUndoEventCallBack = (callback: (canUndo: boolean) => void) => {
        this.newPresentationStoreService?.getEventManager().commandEvents.onUndoRedoChange(({ canUndo }) => {
            callback(canUndo);
        });
    };

    /**
     * 删除主题
     */
    public removeCustomThemeById = (themeId: string) => {
        const themeList = this.customColorList.concat();
        const newThemeList = themeList.filter(item => item.id !== themeId);
        userLocalStorage.setItem(LocalStorageKeys.LOCAL_SLIDE_CUSTOM_THEME, newThemeList);
        this.customColorList = newThemeList;
    };

    /**
     * 删除自定义主题时，清理当前编辑的主题key
     */
    public removeCustomThemeByCurrentEditKey = () => {
        if (this.currentEditThemeKey) {
            this.removeCustomThemeById(this.currentEditThemeKey);
        }
    };

    /**
     * 设置当前编辑的主题key
     * @param currentEditThemeKey
     */
    public setCurrentEditKey = (currentEditThemeKey: string) => {
        this.currentEditThemeKey = currentEditThemeKey;
    };

    /**
     * 文本颜色主题化
     * 将每一个textRun的颜色在主题色彩空间中做一次模糊匹配
     * @param shapeModel
     * @param convertOption
     * @private
     */
    public handleTextColorToTheme(shapeModel: IShapeModel, convertOption?: IConvertOptions) {
        const { paragraphList } = shapeModel;
        const customColor = convertOption?.fontColor;
        if (paragraphList?.length) {
            const cloneParagraphList = cloneDeep(paragraphList);
            cloneParagraphList.forEach((paragraph: IParagraph) => {
                const { textRuns } = paragraph;
                textRuns?.forEach((textRun: ITextRun) => {
                    const { styles } = textRun;
                    if (styles) {
                        const { tdColor } = styles;
                        if (tdColor) {
                            // 如果已经是主题了，就不再映射了
                            if (STSchemeColorVal[tdColor.colorValue]) {
                                return;
                            }
                            const newTdColor = customColor || this.getTdColorWithTheme(tdColor, true);

                            styles.tdColor = newTdColor;
                            const srgbColor = this.currentThemeModel?.clrScheme[newTdColor.colorValue];
                            if (srgbColor) {
                                styles.color = TDColor.lumToHex(srgbColor, newTdColor.lumOff, newTdColor.lumMod);
                            }
                        }
                    }
                });
            });

            return cloneParagraphList;
        }
    }

    /**
     * 更新客户端的自定义主题色
     * @param val
     */
    public set customColorList(val) {
        this.originCustomColorList = val;
        userLocalStorage.setItem(LocalStorageKeys.LOCAL_SLIDE_CUSTOM_THEME, val);
    }

    /**
     * 获取客户端的自定义主题色
     */
    @computed public get customColorList() {
        return this.originCustomColorList;
    }

    /**
     * 更新自定义主题
     */
    public updateCustomTheme = (props: IThemeSelectItem) => {
        const themeList = this.customColorList.concat();
        const sameTitleThemeList = themeList.filter(item => item.title === props.title && item.id !== props.id);
        const oldIndex = themeList.findIndex(item => item.id === props.id);

        // 无重名，清空subTitle
        if (!sameTitleThemeList?.length) {
            props.subTitle = undefined;
        } else if (!props.subTitle) {
            // 有重名，增加subTitle
            let index = 1;
            const tempMap = {};
            sameTitleThemeList.forEach((sameItem) => {
                if (sameItem.subTitle) {
                    tempMap[sameItem.subTitle] = true;
                }
            });

            while (tempMap[index]) {
                index += 1;
            }

            props.subTitle = index;
        }
        themeList.splice(oldIndex, 1, props);
        this.customColorList = themeList;
    };

    /**
     * 增加自定义主题
     * @param props
     */
    public addCustomTheme = (props: Omit<IThemeSelectItem, 'id'>): IThemeSelectItem => {
        const themeList = this.customColorList.concat();
        const id = `${LOCAL_SLIDE_CUSTOM_THEME_ID}_${Date.now()}`;
        const sameTitleThemeList = themeList.filter(item => item.title === props.title);

        // 无重名，清空subTitle
        if (!sameTitleThemeList?.length) {
            props.subTitle = undefined;
        } else if (!props.subTitle) {
            // 有重名，增加subTitle
            let index = 1;
            const tempMap = {};
            sameTitleThemeList.forEach((sameItem) => {
                if (sameItem.subTitle) {
                    tempMap[sameItem.subTitle] = true;
                }
            });

            while (tempMap[index]) {
                index += 1;
            }

            props.subTitle = index;
        }

        const newCustomTheme: IThemeSelectItem = {
            ...props,
            id,
        };

        themeList.push(newCustomTheme);
        this.customColorList = themeList;
        return newCustomTheme;
    };

    /**
     * 根据id获取自定义主题
     * @param themeKey
     */
    public getCustomThemeById = (themeKey: string): IThemeSelectItem | undefined => {
        const themeList = this.customColorList;
        return themeList.find(item => item.id === themeKey);
    };

    /**
     * 获取所有的自定义主题
     */
    public getAllCustomTheme = (): Array<IThemeSelectItem> => {
        const localList = userLocalStorage.getItem(LocalStorageKeys.LOCAL_SLIDE_CUSTOM_THEME);
        const themeList: Array<IThemeSelectItem> = [];
        if (localList?.length) {
            themeList.push(...localList);
        }

        return themeList;
    };

    /**
     * 主题映射
     * 因为大部分普通文档是无主题的，所以单独的主题切换在大部分文档是看不出来效果的
     * 通过遍历所有元素，基于色相-亮度讲文档遍历到的颜色模糊匹配到他最相近的主题，并将颜色映射成对应的主题key
     * 目前匹配规则是基于腾讯文档主题来划分主题匹配区见的，后续可以根据效果看看要不要用office默认主题来匹配
     */
    public convertCustomColorToTheme = (isPreview: boolean, options?: IConvertOptions) => {
        // 首次切换主题预览时做一次映射转换，后续再预览就不转换了，关闭/打开对话框会重置这个状态
        if (this.isConvert && isPreview) return;
        const ignorePlaceholder = !!options?.ignorePlaceholder;
        let customUiDataService: IUiDataService;
        if (isPreview && this.cloneUiDataService) {
            customUiDataService = this.cloneUiDataService;
        } else {
            customUiDataService = this.uiDataService;
        }

        // 枚举slide遍历
        this.handleSlideModelConvert(customUiDataService);

        // 枚举元素遍历
        if (this.slideContentEnumeratorService) {
            this.slideContentEnumeratorService.enumerateWithCallbackSync({
                elementCallback: (elementNode) => {
                    const { element } = elementNode;
                    if (ignorePlaceholder && element.isPlaceholder) return;
                    this.handleElementConvert(element, options);
                },
                tableCellCallback: (tableCell) => {
                    const { element, cell, row: rowIndex, col: colIndex } = tableCell;
                    this.handleTableModelConvert(element as ITableModel, cell, rowIndex, colIndex);
                },
            }, {
                stopTextBlockParentBoxCallback: true,
                customUiDataService,
            });
            this.isConvert = true;
        }
    };

    /**
     * 元素的主题化映射
     * @param element
     * @param options
     */
    public handleElementConvert = (element: IElementBaseModel, options?: IConvertOptions) => {
        // 形状映射
        if (element.type === UIModelType.SHAPE) {
            this.handleShapeModelConvert(element as IShapeModel, options);
        }

        // 组合映射，走递归
        if (element.type === UIModelType.GROUP) {
            this.handleGroupModelConvert(element as IGroupModel, options);
        }

        // todo: 连接线映射

        // todo: 图表映射
    };

    /**
     * 组合的主题化映射
     * @param groupModel
     * @param options
     */
    public handleGroupModelConvert = (groupModel: IGroupModel, options?: IConvertOptions) => {
        groupModel.children.forEach((element) => {
            if (element.type === UIModelType.GROUP) {
                this.handleGroupModelConvert(element as IGroupModel);
            }

            if (element.type === UIModelType.SHAPE) {
                this.handleShapeModelConvert(element as IShapeModel, options);
            }
        });
    };

    /**
     * 获取当前页的主题与明暗信息
     */
    public updateCurrentThemeModel = () => {
        const masterModel = this.selectionService.currentSlide?.getRelationMasterModel();
        this.currentThemeModel = masterModel?.getTheme();
        this.isDark = masterModel?.clrMap?.tx1 === STColorSchemeIndex.lt1;
    };

    private initReaction = () => {
        this.reaction = reaction(() => [this.selectionService.currentSlide], () => {
            this.updateCurrentThemeModel();
        });
        SlideDataServices.SDKDataServices.EventManager()
            ?.commandEvents.onUndoRedoChange((args) => {
                if (args.source === CommandSource.Undo || args.source === CommandSource.Redo) {
                    this.updateCurrentThemeModel();
                }
            });
    };

    /**
     * 清理ai接口的回调
     * @private
     */
    private clearAICallback() {
        this.onSelect = undefined;
        this.onCancel = undefined;
    }

    /**
     * 转换背景填充主题
     * @param shapeModel
     * @private
     */
    private handleShapeBackgroundFill(shapeModel: IShapeModel) {
        const { solidFill, gradFill } = shapeModel.props.style?.fillBackground || {};

        // 纯色填充
        if (solidFill) {
            const newSolidFill = this.handleSolidFill(solidFill);
            if (newSolidFill) {
                shapeModel.getBackgroundFill().updateCurrentFill({ solidFill: newSolidFill }, false);
            }
        }

        // 渐变填充转换
        if (gradFill) {
            const newGradFill = this.handleGradFill(gradFill);
            shapeModel.getBackgroundFill().updateCurrentFill({ gradFill: newGradFill }, false);
        }
    }

    /**
     * 映射背景纯色填充
     * @param solidFill
     * @private
     */
    private handleSolidFill(solidFill: ISolidFill) {
        const { color } = solidFill;

        // 如果已经是主题了，就不再映射了
        if (STSchemeColorVal[color.colorValue]) {
            return;
        }

        const tdColor = this.getTdColorWithTheme(color);
        return new SolidFill(tdColor);
    }

    /**
     * 映射背景渐变填充
     * @param gradFill
     * @private
     */
    private handleGradFill(gradFill: IGradFill) {
        const newGradFill = cloneDeep(gradFill);
        const { gsList } = newGradFill;
        gsList?.forEach((gradFillPoint) => {
            const { color } = gradFillPoint;
            // 如果已经是主题了，就不再映射了
            if (!color || STSchemeColorVal[color.colorValue]) {
                return;
            }

            gradFillPoint.color = this.getTdColorWithTheme(color);
        });

        return newGradFill;
    }

    /**
     * 自定义色映射主题key
     * @param color
     * @param isText
     * @private
     */
    private getTdColorWithTheme(color: TDColor, isText?: boolean) {
        const { hsl } = color;

        // 遍历列，找到最相近色相来判断颜色属于哪个主题
        let colItemList: Array<Array<IColorMapItem>> = [];

        // 低饱和度优先映射黑白，高饱和度映射彩色
        if (hsl.S < LOW_SATURATION_NUMBER) {
            colItemList = this.getWhiteBlackWithTheme(hsl, isText);
        } else {
            colItemList = this.handleHueAndColColor(hsl);
        }

        // 遍历行，找到最相近的发光
        const rowItem = this.handleLumAndRowColor(colItemList, hsl);
        const { lumMod, lumOff, key } = rowItem;
        return new TDColor({
            themeKey: this.currentThemeModel.getMasterColorKeyFromTheme(key, this.isDark),
            lumOff,
            lumMod,
            alpha: color.alpha,
        });
    }

    /**
     * 根据亮度映射主题
     * @param colItemList
     * @param hsl
     * @private
     */
    private handleLumAndRowColor(colItemList: Array<Array<IColorMapItem>>, hsl: IHSL): IColorMapItem {
        let rowItem: any;
        let diffL: number;
        colItemList.forEach((colItem) => {
            colItem.forEach((item) => {
                const newDiffL = Math.abs(item.hsl.L - hsl.L);
                if (!rowItem) {
                    rowItem = item;
                    diffL = newDiffL;
                    return;
                }

                if (newDiffL < diffL) {
                    rowItem = item;
                    diffL = newDiffL;
                }
            });
        });
        return rowItem;
    }

    /**
     * 低饱和度映射黑白
     * @param hsl
     * @param isText
     * @private
     */
    private getWhiteBlackWithTheme(hsl: IHSL, isText?: boolean): Array<Array<IColorMapItem>> {
        const colItemList: Array<Array<IColorMapItem>> = [];
        let colItem: Array<IColorMapItem> | undefined = undefined;
        let diffH: number;
        COLOR_MAP.slice(0, 4).forEach((themeCol, index) => {
            const firstItem = themeCol[0];
            const cloneThemeCol = cloneDeep(themeCol);
            const newDiffH = Math.abs(firstItem.hsl.H - hsl.H);
            if (newDiffH === 0) {
                colItemList.push(cloneThemeCol);
                diffH = newDiffH;
                return;
            }

            if (!colItem) {
                colItem = cloneThemeCol;
                diffH = newDiffH;
                return;
            }

            if (newDiffH < diffH) {
                colItem = cloneThemeCol;
                diffH = newDiffH;
            }
        });

        if (!colItemList.length && colItem) {
            colItemList.push(colItem);
        }

        // 做个策略，如果是黑白色的文本，那么就给他定死在tx1，避免黑白问题
        if (isText) {
            colItemList.forEach((colorMapItemList) => {
                colorMapItemList.forEach((colorItem) => {
                    colorItem.key = STSchemeColorVal.tx1;
                });
            });
        }

        return colItemList;
    }

    /**
     * 高饱和度根据色相映射主题
     * @param hsl
     * @private
     */
    private handleHueAndColColor(hsl: IHSL): Array<Array<IColorMapItem>> {
        const colItemList: Array<Array<IColorMapItem>> = [];
        let colItem: Array<IColorMapItem> | undefined = undefined;
        let diffH: number;
        COLOR_MAP.slice(4).forEach((themeCol, index) => {
            const firstItem = themeCol[0];
            const newDiffH = Math.abs(firstItem.hsl.H - hsl.H);
            if (newDiffH === 0) {
                colItemList.push(themeCol);
                diffH = newDiffH;
                return;
            }

            if (!colItem) {
                colItem = themeCol;
                diffH = newDiffH;
                return;
            }

            if (newDiffH < diffH) {
                colItem = themeCol;
                diffH = newDiffH;
            }
        });

        if (!colItemList.length && colItem) {
            colItemList.push(colItem);
        }

        return colItemList;
    }

    /**
     * 转换表格主题
     * @param tableModel
     * @param cellModel
     * @param rowIndex
     * @param colIndex
     */
    private handleTableModelConvert = (
        tableModel: ITableModel,
        cellModel: IShapeModel,
        rowIndex: number,
        colIndex: number,
    ) => {
        const sdkDataV3 = tableModel.sdkData as unknown as TableDataModel;

        const { solidFill, gradFill } = cellModel.props.style?.fillBackground || {};
        const item: any = {};
        // 纯色填充
        if (solidFill) {
            const newSolidFill = this.handleSolidFill(solidFill);
            if (newSolidFill) {
                _set(item, 'props.style.fillBackground', {
                    solidFill: newSolidFill,
                });
            }
        }

        // 渐变填充转换
        if (gradFill) {
            const newGradFill = this.handleGradFill(gradFill);
            if (newGradFill) {
                _set(item, 'props.style.fillBackground', {
                    gradFill: newGradFill,
                });
            }
        }

        if (item.props) {
            const sdkCell: ICTTableCell = {};
            TableCellPropertyConverter.convertPartialDataFromUiToSdk(item, sdkCell);
            const range = { startRowIndex: rowIndex, startColIndex: colIndex, rowSpan: 1, colSpan: 1 };
            if (sdkCell.tcPr) {
                // 中台默认传递array是append模式，上层纠正为set或者modify模式
                changeArrAppendToSetOrModify(sdkCell.tcPr);
                sdkDataV3.api.updateTableCellProperties(sdkCell.tcPr, range);
            }
        }

        const paragraphList = this.handleTextColorToTheme(cellModel);
        const cell = {
            paragraphList,
        };

        const submitCellSdkData: ICTTextBody = {};
        TextConverter.convertPartialDataFromUiToSdk(cell, submitCellSdkData);
        TableModel.sdkV3UpdateCellParagraphList(
            sdkDataV3,
            submitCellSdkData,
            colIndex,
            rowIndex,
        );
    };

    /**
     * 转换slide背景填充主题
     * @param uiDataService
     */
    private handleSlideModelConvert = (uiDataService: IUiDataService) => {
        const slideList = uiDataService.slideModelList;
        slideList?.forEach((slideModel) => {
            const { fillBackground } = slideModel.props.style;
            const { solidFill, gradFill } = fillBackground || {};

            // 纯色填充
            if (solidFill) {
                const newSolidFill = this.handleSolidFill(solidFill);
                if (newSolidFill) {
                    slideModel.getBackgroundFill().updateCurrentFillForCurrentModel({ solidFill: newSolidFill });
                }
            }

            // 渐变填充转换
            if (gradFill) {
                const newGradFill = this.handleGradFill(gradFill);
                if (newGradFill) {
                    slideModel.getBackgroundFill().updateCurrentFillForCurrentModel({ gradFill: newGradFill });
                }
            }
        });
    };

    /**
     * 转换形状遍历元素属性
     * @param shapeModel
     */
    private handleShapeModelConvert = (shapeModel: IShapeModel, convertOptions?: IConvertOptions) => {
        // 背景填充转换
        this.handleShapeBackgroundFill(shapeModel);

        // 边框填充转换
        this.handleShapeBorderConvert(shapeModel);

        // 文本填充转换
        const cloneParagraphList = this.handleTextColorToTheme(shapeModel, convertOptions);
        shapeModel.updateParagraphList(cloneParagraphList);

        // 特效
    };

    private handleShapeBorderConvert = (shapeModel: IShapeModel) => {
        const borderInfo = shapeModel.getBorder()?.getBorderInfo();
        if (borderInfo?.tdColor) {
            if (!STSchemeColorVal[borderInfo.tdColor.colorValue]) {
                const tdColor = this.getTdColorWithTheme(borderInfo.tdColor);
                borderInfo.tdColor = tdColor;
                borderInfo.color = tdColor.srgb_color;
                shapeModel.getBorder?.()?.updateCurrentBorder(borderInfo, false, false);
            }
        }
    };
}
